// Rotas falsas de Tenancy (GIY-046; Parte 4.6): painel e tenants da rede, ciclo de vida com duas chaves, registro
// societário, e as rotas do tenant (dados jurídicos, sites com localização, calendário/feriados/fechamentos, contrato,
// configurações com origem, bootstrap). Regras de escopo iguais às do servidor: `/network/*` sem acesso de rede →
// 403 PLT_NETWORK_SCOPE_REQUIRED; `/tenants/{t}` fora dos contextos e sem escopo de rede → 404; `If-Match` nos `PUT`;
// razão obrigatória em todo dado regulado.
import type { MeResponse, components } from '@giyus/api-client';

import { FakeApiError, jsonResponse, noContent, problemResponse, readJsonBody } from './http';
import {
  type CalendarVersionResponse,
  type ClosureResponse,
  DEFAULT_WEEKLY,
  type FakeSite,
  type FakeTenancyState,
  type FakeTenant,
  type HolidayResponse,
  type ImpactedAppointment,
  type OwnershipRecordResponse,
  type TimeRange,
  type TransitionResponse,
  addDays,
  civilDate,
  etagOf,
  fakeTenancyId,
  formatCnpj,
  toContractResponse,
  toLegalEntityResponse,
  toSiteResponse,
  toTenantDetail,
} from './tenancyStore';

type ChangeReason = components['schemas']['TenancyChangeReasonRequest'];
type SettingValueResponse = components['schemas']['SettingValueResponse'];

export interface TenancyRouteContext {
  readonly state: FakeTenancyState;
  readonly me: MeResponse;
  readonly correlationId: string | null;
}

const UF = new Set([
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
]);
const TIME_ZONES = new Set([
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Belem',
  'America/Fortaleza',
  'America/Recife',
  'America/Bahia',
  'America/Cuiaba',
  'America/Campo_Grande',
  'America/Porto_Velho',
  'America/Boa_Vista',
  'America/Rio_Branco',
  'America/Noronha',
  'UTC',
]);
const WEEK_DAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;
const KNOWN_DAYS = new Set<string>(WEEK_DAYS);
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Feriados nacionais fixos (os móveis — Carnaval, Sexta-feira Santa, Corpus Christi — só o servidor calcula). */
const NATIONAL_HOLIDAYS: Readonly<Record<string, string>> = {
  '01-01': 'Confraternização Universal',
  '04-21': 'Tiradentes',
  '05-01': 'Dia do Trabalho',
  '09-07': 'Independência do Brasil',
  '10-12': 'Nossa Senhora Aparecida',
  '11-02': 'Finados',
  '11-15': 'Proclamação da República',
  '11-20': 'Dia da Consciência Negra',
  '12-25': 'Natal',
};
const NEXT_STATUSES: Readonly<Record<string, readonly string[]>> = {
  Draft: ['Onboarding'],
  Onboarding: ['Active'],
  Active: ['Suspended', 'Offboarding'],
  Suspended: ['Active', 'Offboarding'],
  Offboarding: [],
  Offboarded: [],
};
const TWO_KEY_TARGETS = new Set(['Suspended', 'Offboarding']);

interface FieldErrorItem {
  readonly field: string;
  readonly code: string;
  readonly message: string;
}

function record(body: unknown): Record<string, unknown> {
  return typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};
}

function text(body: unknown, field: string): string | null {
  const value = record(body)[field];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function validation(errors: readonly FieldErrorItem[], errorCode = 'PLT_VALIDATION'): FakeApiError {
  return new FakeApiError(422, errorCode, { errors });
}

function requireText(body: unknown, field: string, errors: FieldErrorItem[]): string {
  const value = text(body, field);
  if (value === null) {
    errors.push({ field, code: 'required', message: 'Required.' });
    return '';
  }
  return value;
}

function requireChangeReason(body: unknown): ChangeReason {
  const reason = record(body)['changeReason'];
  const code =
    typeof reason === 'object' && reason !== null ? (reason as { code?: unknown }).code : null;
  if (typeof code !== 'string' || code.trim() === '') {
    throw new FakeApiError(422, 'PLT_CHANGE_REASON_REQUIRED');
  }
  const reasonText = (reason as { text?: unknown }).text;
  return { code, text: typeof reasonText === 'string' ? reasonText : null };
}

function requireIfMatch(request: Request, current: string): void {
  const header = request.headers.get('If-Match');
  if (!header) {
    throw new FakeApiError(412, 'PLT_PRECONDITION_REQUIRED');
  }
  if (header !== '*' && header !== current) {
    throw new FakeApiError(412, 'PLT_PRECONDITION_FAILED');
  }
}

function findTenant(state: FakeTenancyState, id: string | undefined): FakeTenant {
  const found = state.tenants.find((tenant) => tenant.id === id);
  if (!found) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return found;
}

function findSite(tenant: FakeTenant, id: string | undefined): FakeSite {
  const found = tenant.sites.find((site) => site.id === id);
  if (!found) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return found;
}

function digits(value: string): string {
  return value.replace(/\D/g, '');
}

// ---- Pessoa jurídica e endereço ----

function parseAddress(body: unknown, prefix: string, errors: FieldErrorItem[]) {
  const street = requireText(body, 'street', errors);
  const number = requireText(body, 'number', errors);
  const district = requireText(body, 'district', errors);
  const city = requireText(body, 'city', errors);
  const state = (text(body, 'state') ?? '').toUpperCase();
  if (!UF.has(state)) {
    throw new FakeApiError(422, 'TEN_INVALID_STATE', {
      errors: [{ field: `${prefix}.state`, code: 'invalid', message: 'Unknown state.' }],
    });
  }
  const postalCode = text(body, 'postalCode') ?? '';
  if (!/^\d{5}-\d{3}$/.test(postalCode)) {
    throw new FakeApiError(422, 'TEN_INVALID_POSTAL_CODE', {
      errors: [{ field: `${prefix}.postalCode`, code: 'invalid', message: 'Expected 00000-000.' }],
    });
  }
  return {
    street,
    number,
    complement: text(body, 'complement'),
    district,
    city,
    state,
    postalCode,
    country: 'BR',
  };
}

function parseLegalEntity(
  state: FakeTenancyState,
  body: unknown,
  current: FakeTenant['legalEntity'] | null,
): Omit<FakeTenant['legalEntity'], 'id' | 'version' | 'kind' | 'taxRegimeExceptionApprovalId'> {
  const errors: FieldErrorItem[] = [];
  const legalName = requireText(body, 'legalName', errors);
  const institutionalEmail = requireText(body, 'institutionalEmail', errors);
  const representative = record(record(body)['legalRepresentative']);
  const representativeName = requireText(representative, 'name', errors);
  const representativeEmail = requireText(representative, 'email', errors);
  const addressBody = record(body)['address'];
  if (typeof addressBody !== 'object' || addressBody === null) {
    errors.push({ field: 'legalEntity.address', code: 'required', message: 'Required.' });
  }
  if (errors.length > 0) {
    throw validation(errors);
  }
  const cnpj = digits(text(body, 'registrationNumber') ?? '');
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) {
    throw new FakeApiError(422, 'TEN_INVALID_CNPJ');
  }
  if (
    state.tenants.some(
      (tenant) =>
        tenant.legalEntity.registrationNumber === cnpj && tenant.legalEntity.id !== current?.id,
    )
  ) {
    throw new FakeApiError(422, 'TEN_DUPLICATE_CNPJ');
  }
  // `LegalEntityRules` exige o CPF na criação e na edição (a API nunca o devolve em claro).
  const cpfRaw = text(representative, 'cpf');
  if (cpfRaw === null) {
    throw validation([
      { field: 'legalEntity.legalRepresentative.cpf', code: 'required', message: 'Required.' },
    ]);
  }
  if (digits(cpfRaw).length !== 11) {
    throw new FakeApiError(422, 'TEN_INVALID_CPF');
  }
  const taxRegime = text(body, 'taxRegime') ?? 'LucroReal';
  return {
    legalName,
    tradeName: text(body, 'tradeName'),
    country: 'BR',
    registrationNumber: cnpj,
    registrationNumberFormatted: formatCnpj(cnpj),
    stateRegistration: text(body, 'stateRegistration'),
    municipalRegistration: text(body, 'municipalRegistration'),
    taxRegime,
    legalRepresentative: {
      name: representativeName,
      cpfMasked: `***.***.***-${digits(cpfRaw).slice(-2)}`,
      email: representativeEmail,
    },
    institutionalEmail,
    institutionalPhone: text(body, 'institutionalPhone'),
    address: parseAddress(addressBody, 'legalEntity.address', errors),
  };
}

// ---- Sites ----

function parseSite(
  tenant: FakeTenant,
  body: unknown,
  base: string,
  existing: FakeSite | null,
): FakeSite {
  const errors: FieldErrorItem[] = [];
  const name = requireText(body, 'name', errors);
  const timeZoneId = requireText(body, 'timeZoneId', errors);
  const addressBody = record(body)['address'];
  if (typeof addressBody !== 'object' || addressBody === null) {
    errors.push({ field: 'address', code: 'required', message: 'Required.' });
  }
  let code = existing?.code ?? '';
  if (!existing) {
    code = (text(body, 'code') ?? '').toUpperCase();
    if (!/^[A-Z0-9-]{2,12}$/.test(code)) {
      throw new FakeApiError(422, 'TEN_INVALID_SITE_CODE');
    }
    if (tenant.sites.some((site) => site.code === code)) {
      throw new FakeApiError(409, 'TEN_DUPLICATE_SITE_CODE');
    }
  }
  if (errors.length > 0) {
    throw validation(errors);
  }
  if (!TIME_ZONES.has(timeZoneId)) {
    throw new FakeApiError(422, 'TEN_INVALID_TIMEZONE');
  }
  const address = parseAddress(addressBody, 'address', errors);
  const location = record(record(body)['location']);
  const ibge = text(location, 'ibgeMunicipalityCode');
  if (ibge !== null && !/^\d{7}$/.test(ibge)) {
    throw new FakeApiError(422, 'TEN_INVALID_MUNICIPALITY');
  }
  const latitude = location['latitude'];
  const longitude = location['longitude'];
  const hasLatitude = typeof latitude === 'number';
  const hasLongitude = typeof longitude === 'number';
  if (hasLatitude !== hasLongitude) {
    throw new FakeApiError(422, 'TEN_INVALID_COORDINATES');
  }
  if (hasLatitude && hasLongitude && (Math.abs(latitude) > 90 || Math.abs(longitude) > 180)) {
    throw new FakeApiError(422, 'TEN_INVALID_COORDINATES');
  }
  const status = text(body, 'status') ?? existing?.status ?? 'Active';
  return {
    id: existing?.id ?? '',
    tenantId: tenant.id,
    code,
    name,
    timeZoneId,
    phone: text(body, 'phone'),
    status: status === 'Planned' ? 'Planned' : status === 'Inactive' ? 'Inactive' : 'Active',
    address,
    state: address.state,
    ibgeMunicipalityCode: ibge,
    regionFreeText: null,
    latitude: hasLatitude ? latitude : null,
    longitude: hasLongitude ? longitude : null,
    locationSource: hasLatitude && hasLongitude ? 'Manual' : 'Address',
    version: existing?.version ?? 1,
    calendarVersions: existing?.calendarVersions ?? [
      {
        id: '',
        siteId: '',
        effectiveFrom: civilDate(base, timeZoneId),
        effectiveTo: null,
        weekly: structuredClone(DEFAULT_WEEKLY),
        note: null,
        createdAt: base,
        createdBy: null,
      },
    ],
    holidays: existing?.holidays ?? [],
    closures: existing?.closures ?? [],
    appointments: existing?.appointments ?? [],
  };
}

// ---- Calendário ----

function validateWeekly(body: unknown): Record<string, TimeRange[]> {
  const weekly = record(body)['weekly'];
  const errors: FieldErrorItem[] = [];
  const result: Record<string, TimeRange[]> = {};
  for (const day of WEEK_DAYS) {
    result[day] = [];
  }
  for (const [day, ranges] of Object.entries(record(weekly))) {
    if (!KNOWN_DAYS.has(day)) {
      errors.push({ field: `weekly.${day}`, code: 'unknown_day', message: 'Unknown day.' });
      continue;
    }
    const list = Array.isArray(ranges) ? ranges : [];
    const parsed: TimeRange[] = [];
    list.forEach((range, index) => {
      const start = text(range, 'start') ?? '';
      const end = text(range, 'end') ?? '';
      if (!TIME.test(start) || !TIME.test(end)) {
        errors.push({
          field: `weekly.${day}[${String(index)}].start`,
          code: 'invalid_time',
          message: 'HH:mm.',
        });
        return;
      }
      if (start >= end) {
        errors.push({
          field: `weekly.${day}[${String(index)}]`,
          code: 'start_not_before_end',
          message: 'Start < end.',
        });
        return;
      }
      parsed.push({ start, end });
    });
    parsed.sort((a, b) => a.start.localeCompare(b.start));
    for (let index = 1; index < parsed.length; index += 1) {
      const previous = parsed[index - 1];
      const current = parsed[index];
      if (previous && current && current.start < previous.end) {
        errors.push({ field: `weekly.${day}`, code: 'overlap', message: 'Overlapping ranges.' });
        break;
      }
    }
    result[day] = parsed;
  }
  if (errors.length > 0) {
    throw validation(errors, 'TEN_CALENDAR_INVALID_RANGES');
  }
  return result;
}

function subtract(ranges: readonly TimeRange[], cut: TimeRange): TimeRange[] {
  const out: TimeRange[] = [];
  for (const range of ranges) {
    if (cut.end <= range.start || cut.start >= range.end) {
      out.push(range);
      continue;
    }
    if (cut.start > range.start) {
      out.push({ start: range.start, end: cut.start });
    }
    if (cut.end < range.end) {
      out.push({ start: cut.end, end: range.end });
    }
  }
  return out;
}

function dayName(date: string): string {
  return WEEK_DAYS[new Date(`${date}T00:00:00Z`).getUTCDay()] ?? 'monday';
}

function effectiveCalendar(site: FakeSite, from: string, to: string) {
  const days = [];
  for (let date = from; date <= to; date = addDays(date, 1)) {
    const version = site.calendarVersions.find(
      (candidate) =>
        candidate.effectiveFrom <= date &&
        (candidate.effectiveTo === null || candidate.effectiveTo >= date),
    );
    let ranges: TimeRange[] = [
      ...(version ? (version.weekly[dayName(date)] ?? []) : (DEFAULT_WEEKLY[dayName(date)] ?? [])),
    ];
    const holidays = [
      ...site.holidays
        .filter((holiday) => holiday.date === date)
        .map((holiday) => ({
          id: holiday.id,
          name: holiday.name,
          scope: holiday.scope,
          closesSite: holiday.closesSite,
        })),
    ];
    const national = NATIONAL_HOLIDAYS[date.slice(5)];
    if (national) {
      holidays.push({
        id: `national-${date}`,
        name: national,
        scope: 'National',
        closesSite: true,
      });
    }
    if (holidays.some((holiday) => holiday.closesSite)) {
      ranges = [];
    }
    const closures = site.closures
      .filter((closure) => closure.startDate <= date && closure.endDate >= date)
      .map((closure) => ({
        id: closure.id,
        reason: closure.reason,
        category: closure.category,
        startTime: closure.startTime,
        endTime: closure.endTime,
      }));
    for (const closure of closures) {
      ranges =
        closure.startTime && closure.endTime
          ? subtract(ranges, { start: closure.startTime, end: closure.endTime })
          : [];
    }
    days.push({
      date,
      isOpen: ranges.length > 0,
      openRanges: ranges,
      holidays,
      closures,
      calendarVersionId: version?.id ?? null,
    });
  }
  return { siteId: site.id, timeZoneId: site.timeZoneId, from, to, days };
}

function parseClosure(site: FakeSite, body: unknown, today: string) {
  const errors: FieldErrorItem[] = [];
  const startDate = text(body, 'startDate') ?? '';
  const endDate = text(body, 'endDate') ?? '';
  const startTime = text(body, 'startTime');
  const endTime = text(body, 'endTime');
  if (!ISO_DATE.test(startDate)) {
    errors.push({ field: 'startDate', code: 'invalid_date', message: 'yyyy-MM-dd.' });
  }
  if (!ISO_DATE.test(endDate)) {
    errors.push({ field: 'endDate', code: 'invalid_date', message: 'yyyy-MM-dd.' });
  } else if (endDate < startDate) {
    errors.push({ field: 'endDate', code: 'before_start', message: 'End before start.' });
  }
  if ((startTime === null) !== (endTime === null)) {
    errors.push({ field: 'startTime', code: 'both_or_none', message: 'Both or none.' });
  } else if (startTime !== null && endTime !== null) {
    if (!TIME.test(startTime) || !TIME.test(endTime)) {
      errors.push({ field: 'startTime', code: 'invalid_time', message: 'HH:mm.' });
    } else if (startTime >= endTime) {
      errors.push({ field: 'startTime', code: 'start_not_before_end', message: 'Start < end.' });
    }
  }
  if (errors.length > 0) {
    throw validation(errors, 'TEN_CLOSURE_INVALID_RANGE');
  }
  const reason = text(body, 'reason');
  if (reason === null) {
    throw validation([{ field: 'reason', code: 'required', message: 'Required.' }]);
  }
  const justification = text(body, 'retroactiveJustification');
  if (startDate < today && justification === null) {
    throw new FakeApiError(422, 'TEN_CLOSURE_RETROACTIVE_JUSTIFICATION_REQUIRED');
  }
  const category = text(body, 'category') ?? 'Other';
  const impact = site.appointments.filter((appointment) => {
    const date = civilDate(appointment.scheduledAt, site.timeZoneId);
    if (date < startDate || date > endDate) {
      return false;
    }
    if (startTime === null || endTime === null) {
      return true;
    }
    const time = new Intl.DateTimeFormat('en-GB', {
      timeZone: site.timeZoneId,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(appointment.scheduledAt));
    return time >= startTime && time < endTime;
  });
  return {
    closure: {
      startDate,
      endDate,
      startTime,
      endTime,
      reason,
      category,
      retroactiveJustification: justification,
    },
    impact: { schedulingAvailable: false, appointments: impact },
  };
}

// ---- Ciclo de vida ----

function activationErrors(tenant: FakeTenant, toStatus: string): FieldErrorItem[] {
  const errors: FieldErrorItem[] = [];
  if (tenant.sites.length === 0) {
    errors.push({ field: 'sites', code: 'site_required', message: 'At least one site.' });
  }
  if (toStatus !== 'Active') {
    return errors;
  }
  if (
    !tenant.sites.some(
      (site) => site.status === 'Active' && site.state !== null && UF.has(site.state),
    )
  ) {
    errors.push({
      field: 'sites',
      code: 'active_site_required',
      message: 'An active site with a valid location.',
    });
  }
  if (tenant.bootstrap?.status !== 'Active') {
    errors.push({
      field: 'admins',
      code: 'active_admin_required',
      message: 'An active tenant administrator.',
    });
  }
  if (tenant.legalEntity.taxRegime !== 'LucroReal') {
    errors.push({
      field: 'legalEntity.taxRegime',
      code: 'lucro_real_required',
      message: 'Lucro Real.',
    });
  }
  if (tenant.admissionPath === null) {
    errors.push({
      field: 'admissionPath',
      code: 'admission_path_required',
      message: 'Admission path.',
    });
  }
  return errors;
}

function applyTransition(tenant: FakeTenant, transition: TransitionResponse, at: string): void {
  tenant.status = transition.toStatus;
  tenant.version += 1;
  if (transition.toStatus === 'Active') {
    tenant.activatedAt ??= at;
    tenant.suspendedAt = null;
  }
  if (transition.toStatus === 'Suspended') {
    tenant.suspendedAt = at;
  }
  transition.status = 'Applied';
  transition.appliedAt = at;
  transition.tenantStatus = tenant.status;
}

// ---- Configurações ----

function settingValue(
  state: FakeTenancyState,
  tenant: FakeTenant,
  key: string,
): SettingValueResponse {
  const definition = state.definitions.find((candidate) => candidate.key === key);
  if (!definition) {
    throw new FakeApiError(404, 'CFG_SETTING_UNKNOWN');
  }
  const override = tenant.settingOverrides.get(key);
  const origin = override
    ? 'Tenant'
    : definition.networkValue !== undefined
      ? 'Network'
      : 'Default';
  return {
    key,
    value: override ? override.value : (definition.networkValue ?? definition.defaultValue),
    origin,
    tenantOverridable: definition.tenantOverridable,
    requiresTwoKeys: definition.requiresTwoKeys,
    eTag: override ? etagOf(override.version, 's') : '*',
  };
}

// ---- Handlers ----

function overview(state: FakeTenancyState, url: URL) {
  const status = url.searchParams.get('status');
  const q = (url.searchParams.get('q') ?? '').trim().toLowerCase();
  const page = Math.max(1, Number(url.searchParams.get('page') ?? '1') || 1);
  const pageSize = Math.min(
    100,
    Math.max(1, Number(url.searchParams.get('pageSize') ?? '20') || 20),
  );
  const filtered = state.tenants
    .filter((tenant) => (status ? tenant.status === status : true))
    .filter((tenant) =>
      q
        ? tenant.code.toLowerCase().includes(q) ||
          tenant.displayName.toLowerCase().includes(q) ||
          tenant.legalEntity.legalName.toLowerCase().includes(q)
        : true,
    )
    .sort((a, b) => a.code.localeCompare(b.code));
  const items = filtered.slice((page - 1) * pageSize, page * pageSize).map((tenant) => ({
    id: tenant.id,
    code: tenant.code,
    displayName: tenant.displayName,
    status: tenant.status,
    defaultLocale: tenant.defaultLocale,
    createdAt: tenant.createdAt,
    activatedAt: tenant.activatedAt,
    legalName: tenant.legalEntity.legalName,
    registrationNumberFormatted: tenant.legalEntity.registrationNumberFormatted,
    siteCount: tenant.sites.length,
    activeSiteCount: tenant.sites.filter((site) => site.status === 'Active').length,
    activeUserCount: tenant.activeUserCount,
    activeStudyCount: null,
    lastExportAt: null,
    bootstrapStatus: tenant.bootstrap?.status ?? 'None',
    bootstrapPending: tenant.bootstrap?.status === 'Pending',
  }));
  return { items, page, pageSize, total: filtered.length };
}

const BOOTSTRAP_LINK_TYPES: readonly string[] = [
  'Socio',
  'CLT',
  'PJ',
  'CooperadoCPESQ',
  'Estagiario',
];

function parseBootstrapInput(body: unknown, prefix: string) {
  const errors: FieldErrorItem[] = [];
  const person = record(body)['person'];
  const personId = text(body, 'personId');
  let email: string | null = null;
  if (personId === null) {
    if (typeof person !== 'object' || person === null) {
      errors.push({ field: `${prefix}person`, code: 'required', message: 'Required.' });
    } else {
      requireText(person, 'givenName', errors);
      requireText(person, 'familyName', errors);
      email = requireText(person, 'email', errors);
      if (email && !email.includes('@')) {
        errors.push({
          field: `${prefix}person.email`,
          code: 'invalid_email',
          message: 'Invalid e-mail.',
        });
      }
    }
  } else if (!UUID.test(personId)) {
    errors.push({ field: `${prefix}personId`, code: 'invalid', message: 'Invalid identifier.' });
  }
  const link = record(body)['link'];
  if (typeof link !== 'object' || link === null) {
    errors.push({ field: `${prefix}link`, code: 'required', message: 'Required.' });
  } else {
    const linkType = requireText(link, 'linkType', errors);
    if (linkType !== '' && !BOOTSTRAP_LINK_TYPES.includes(linkType)) {
      errors.push({
        field: `${prefix}link.linkType`,
        code: 'invalid',
        message: 'Invalid link type.',
      });
    }
    const effectiveFrom = text(link, 'effectiveFrom');
    if (effectiveFrom === null || !ISO_DATE.test(effectiveFrom)) {
      errors.push({ field: `${prefix}link.effectiveFrom`, code: 'required', message: 'Required.' });
    }
  }
  if (errors.length > 0) {
    throw validation(
      errors.map((error) => ({
        ...error,
        field: error.field.startsWith(prefix) ? error.field : `${prefix}${error.field}`,
      })),
    );
  }
  const lockedLinkType = text(record(body)['link'], 'linkType');
  // Matriz de Enquadramento da função TENANT_ADMIN_BOOTSTRAP (mesma linha da central da holding): só Sócio e CLT são indicados.
  if (lockedLinkType === 'PJ') {
    throw new FakeApiError(423, 'PPL_LINK_OPINION_REQUIRED');
  }
  if (lockedLinkType === 'CooperadoCPESQ' || lockedLinkType === 'Estagiario') {
    throw new FakeApiError(423, 'PPL_LINK_NOT_ALLOWED');
  }
  return { personId, email };
}

function defineBootstrap(
  state: FakeTenancyState,
  tenant: FakeTenant,
  body: unknown,
  prefix: string,
) {
  const input = parseBootstrapInput(body, prefix);
  const existing = tenant.bootstrap;
  if (existing && input.personId !== null && existing.personId === input.personId) {
    return { status: 200, bootstrap: existing };
  }
  if (existing?.status === 'Pending') {
    existing.status = 'Superseded';
  }
  const created = {
    id: fakeTenancyId(state),
    tenantId: tenant.id,
    personId: input.personId ?? fakeTenancyId(state),
    userId: fakeTenancyId(state),
    invitationId: fakeTenancyId(state),
    roleCode: 'TENANT_ADMIN',
    status: 'Pending',
    createdAt: state.base,
    activatedAt: null,
  };
  tenant.bootstrap = created;
  return { status: 201, bootstrap: created };
}

function createTenant(state: FakeTenancyState, body: unknown): FakeTenant {
  const code = (text(body, 'code') ?? '').toUpperCase();
  if (!/^[A-Z0-9]{3,8}$/.test(code)) {
    throw new FakeApiError(422, 'TEN_INVALID_TENANT_CODE');
  }
  if (state.tenants.some((tenant) => tenant.code === code)) {
    throw new FakeApiError(409, 'TEN_DUPLICATE_TENANT_CODE');
  }
  const displayName = text(body, 'displayName');
  if (displayName === null) {
    throw validation([{ field: 'displayName', code: 'required', message: 'Required.' }]);
  }
  const legalEntityBody = record(body)['legalEntity'];
  if (typeof legalEntityBody !== 'object' || legalEntityBody === null) {
    throw validation([{ field: 'legalEntity', code: 'required', message: 'Required.' }]);
  }
  const parsedLegalEntity = parseLegalEntity(state, legalEntityBody, null);
  const admissionPath = text(body, 'admissionPath');
  const planDocument = text(body, 'conformancePlanDocumentId');
  const planDeadline = text(body, 'conformancePlanDeadline');
  if (admissionPath === 'ModoDeExcecao') {
    const errors: FieldErrorItem[] = [];
    if (planDocument === null || !UUID.test(planDocument)) {
      errors.push({
        field: 'conformancePlanDocumentId',
        code: 'conformance_plan_document_required',
        message: 'Required.',
      });
    }
    if (planDeadline === null || !ISO_DATE.test(planDeadline)) {
      errors.push({
        field: 'conformancePlanDeadline',
        code: 'conformance_plan_deadline_required',
        message: 'Required.',
      });
    }
    if (errors.length > 0) {
      throw validation(errors, 'TEN_CONFORMANCE_PLAN_INVALID');
    }
  }
  const tenant: FakeTenant = {
    id: fakeTenancyId(state),
    code,
    displayName,
    defaultLocale: text(body, 'defaultLocale') ?? 'pt-BR',
    status: 'Draft',
    admissionPath,
    conformancePlanDocumentId: admissionPath === 'ModoDeExcecao' ? planDocument : null,
    conformancePlanDeadline: admissionPath === 'ModoDeExcecao' ? planDeadline : null,
    createdAt: state.base,
    activatedAt: null,
    suspendedAt: null,
    offboardedAt: null,
    version: 1,
    legalEntity: {
      ...parsedLegalEntity,
      id: fakeTenancyId(state),
      kind: 'Center',
      taxRegimeExceptionApprovalId: null,
      version: 1,
    },
    sites: [],
    transitions: [],
    bootstrap: null,
    contract: null,
    settingOverrides: new Map(),
    activeUserCount: 0,
  };
  const sites = record(body)['sites'];
  for (const siteBody of Array.isArray(sites) ? sites : []) {
    const parsed = parseSite(tenant, siteBody, state.base, null);
    parsed.id = fakeTenancyId(state);
    for (const version of parsed.calendarVersions) {
      version.id = fakeTenancyId(state);
      version.siteId = parsed.id;
    }
    tenant.sites.push(parsed);
  }
  const initialAdmin = record(body)['initialAdmin'];
  if (typeof initialAdmin === 'object' && initialAdmin !== null) {
    defineBootstrap(state, tenant, initialAdmin, 'initialAdmin.');
  }
  state.tenants.push(tenant);
  return tenant;
}

async function handleNetworkRoutes(
  request: Request,
  rest: readonly string[],
  ctx: TenancyRouteContext,
): Promise<Response | null> {
  const method = request.method.toUpperCase();
  const { state } = ctx;
  const actorId = ctx.me.user.id;
  const url = new URL(request.url);

  if (rest[0] === 'tenants-overview' && rest.length === 1 && method === 'GET') {
    return jsonResponse(overview(state, url));
  }
  if (rest[0] === 'ownership-records') {
    if (rest.length === 1 && method === 'GET') {
      const legalEntityId = url.searchParams.get('legalEntityId');
      const status = url.searchParams.get('status');
      return jsonResponse(
        state.ownership
          .filter((entry) => (legalEntityId ? entry.ownedLegalEntityId === legalEntityId : true))
          .filter((entry) => (status ? entry.status === status : true))
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
      );
    }
    if (rest.length === 1 && method === 'POST') {
      return jsonResponse(createOwnership(state, await readJsonBody(request)), 201);
    }
    if (rest.length === 3 && rest[2] === 'end' && method === 'POST') {
      const body = await readJsonBody(request);
      const found = state.ownership.find((entry) => entry.id === rest[1]);
      if (!found) {
        throw new FakeApiError(404, 'PLT_NOT_FOUND');
      }
      requireChangeReason(body);
      if (found.status !== 'Active') {
        throw new FakeApiError(409, 'TEN_OWNERSHIP_NOT_ACTIVE');
      }
      const effectiveTo = text(body, 'effectiveTo');
      if (
        effectiveTo === null ||
        !ISO_DATE.test(effectiveTo) ||
        effectiveTo < found.effectiveFrom
      ) {
        throw validation([
          { field: 'effectiveTo', code: 'before_from', message: 'Before effectiveFrom.' },
        ]);
      }
      const ended: OwnershipRecordResponse = { ...found, effectiveTo, status: 'Ended' };
      state.ownership.splice(state.ownership.indexOf(found), 1, ended);
      return jsonResponse(ended);
    }
    return null;
  }
  if (rest[0] !== 'tenants') {
    return null;
  }
  if (rest.length === 1 && method === 'GET') {
    const page = overview(state, url);
    return jsonResponse({
      items: page.items.map((item) => ({
        id: item.id,
        code: item.code,
        displayName: item.displayName,
        status: item.status,
        defaultLocale: item.defaultLocale,
        legalName: item.legalName,
        registrationNumberFormatted: item.registrationNumberFormatted,
        siteCount: item.siteCount,
        createdAt: item.createdAt,
      })),
      page: page.page,
      pageSize: page.pageSize,
      total: page.total,
    });
  }
  if (rest.length === 1 && method === 'POST') {
    const tenant = createTenant(state, await readJsonBody(request));
    return jsonResponse(toTenantDetail(tenant), 201, { ETag: etagOf(tenant.version) });
  }
  const tenant = findTenant(state, rest[1]);
  if (rest.length === 2 && method === 'GET') {
    return jsonResponse(toTenantDetail(tenant), 200, { ETag: etagOf(tenant.version) });
  }
  if (rest.length === 2 && method === 'PUT') {
    requireIfMatch(request, etagOf(tenant.version));
    const body = await readJsonBody(request);
    requireChangeReason(body);
    const displayName = text(body, 'displayName');
    if (displayName === null) {
      throw validation([{ field: 'displayName', code: 'required', message: 'Required.' }]);
    }
    const admissionPath = text(body, 'admissionPath');
    const planDocument = text(body, 'conformancePlanDocumentId');
    const planDeadline = text(body, 'conformancePlanDeadline');
    if (admissionPath === 'ModoDeExcecao' && (planDocument === null || planDeadline === null)) {
      throw new FakeApiError(422, 'TEN_CONFORMANCE_PLAN_INVALID');
    }
    tenant.displayName = displayName;
    tenant.defaultLocale = text(body, 'defaultLocale') ?? tenant.defaultLocale;
    tenant.admissionPath = admissionPath ?? tenant.admissionPath;
    tenant.conformancePlanDocumentId = admissionPath === 'ModoDeExcecao' ? planDocument : null;
    tenant.conformancePlanDeadline = admissionPath === 'ModoDeExcecao' ? planDeadline : null;
    tenant.version += 1;
    return jsonResponse(toTenantDetail(tenant), 200, { ETag: etagOf(tenant.version) });
  }
  if (rest[2] === 'lifecycle' && rest[3] === 'transitions') {
    if (rest.length === 4 && method === 'GET') {
      return jsonResponse(
        [...tenant.transitions].sort((a, b) => b.decidedAt.localeCompare(a.decidedAt)),
      );
    }
    if (rest.length === 4 && method === 'POST') {
      const body = await readJsonBody(request);
      const reason = requireChangeReason(body);
      const toStatus = text(body, 'toStatus') ?? '';
      if (!(NEXT_STATUSES[tenant.status] ?? []).includes(toStatus)) {
        throw new FakeApiError(409, 'TEN_INVALID_TRANSITION');
      }
      if (tenant.transitions.some((entry) => entry.status === 'PendingSecondKey')) {
        throw new FakeApiError(409, 'TEN_TRANSITION_PENDING');
      }
      const errors = activationErrors(tenant, toStatus);
      if (errors.length > 0) {
        throw validation(errors, 'TEN_TRANSITION_PREREQUISITES_NOT_MET');
      }
      const requiresTwoKeys = TWO_KEY_TARGETS.has(toStatus);
      const created: TransitionResponse = {
        id: fakeTenancyId(state),
        tenantId: tenant.id,
        fromStatus: tenant.status,
        toStatus,
        reasonCode: reason.code,
        reasonText: reason.text,
        status: 'PendingSecondKey',
        requiresTwoKeys,
        twoKeyApprovalId: requiresTwoKeys ? fakeTenancyId(state) : null,
        decidedBy: actorId,
        decidedAt: state.base,
        secondKeyBy: null,
        secondKeyAt: null,
        appliedAt: null,
        tenantStatus: tenant.status,
      };
      if (!requiresTwoKeys) {
        applyTransition(tenant, created, state.base);
      }
      tenant.transitions.push(created);
      return jsonResponse(created, 201);
    }
    if (rest.length === 6 && rest[5] === 'second-key' && method === 'POST') {
      const body = await readJsonBody(request);
      const found = tenant.transitions.find((entry) => entry.id === rest[4]);
      if (!found) {
        throw new FakeApiError(404, 'PLT_NOT_FOUND');
      }
      if (found.status !== 'PendingSecondKey') {
        throw new FakeApiError(409, 'TEN_TRANSITION_NOT_PENDING');
      }
      if (found.decidedBy === actorId) {
        throw new FakeApiError(422, 'AUTHZ_TWO_KEY_SAME_ACTOR');
      }
      found.secondKeyBy = actorId;
      found.secondKeyAt = state.base;
      found.reasonText = text(body, 'comment') ?? found.reasonText;
      applyTransition(tenant, found, state.base);
      return jsonResponse(found);
    }
    return null;
  }
  return null;
}

function createOwnership(state: FakeTenancyState, body: unknown): OwnershipRecordResponse {
  const errors: FieldErrorItem[] = [];
  const owned = text(body, 'ownedLegalEntityId');
  const knownLegalEntities = new Set([
    state.holdingLegalEntityId,
    ...state.tenants.map((tenant) => tenant.legalEntity.id),
  ]);
  if (owned === null) {
    errors.push({ field: 'ownedLegalEntityId', code: 'required', message: 'Required.' });
  } else if (!knownLegalEntities.has(owned)) {
    errors.push({ field: 'ownedLegalEntityId', code: 'unknown', message: 'Unknown legal entity.' });
  }
  const holderKind = text(body, 'holderKind');
  const holderPersonId = text(body, 'holderPersonId');
  const holderLegalEntityId = text(body, 'holderLegalEntityId');
  if (holderKind === 'Person') {
    if (holderPersonId === null) {
      errors.push({ field: 'holderPersonId', code: 'required', message: 'Required.' });
    }
    if (holderLegalEntityId !== null) {
      errors.push({
        field: 'holderLegalEntityId',
        code: 'not_allowed_for_person_holder',
        message: 'Not allowed.',
      });
    }
  } else if (holderKind === 'LegalEntity') {
    if (holderLegalEntityId === null) {
      errors.push({ field: 'holderLegalEntityId', code: 'required', message: 'Required.' });
    } else if (!knownLegalEntities.has(holderLegalEntityId)) {
      errors.push({
        field: 'holderLegalEntityId',
        code: 'unknown',
        message: 'Unknown legal entity.',
      });
    } else if (holderLegalEntityId === owned) {
      errors.push({
        field: 'holderLegalEntityId',
        code: 'self_ownership',
        message: 'Self ownership.',
      });
    }
    if (holderPersonId !== null) {
      errors.push({
        field: 'holderPersonId',
        code: 'not_allowed_for_legal_entity_holder',
        message: 'Not allowed.',
      });
    }
  } else {
    errors.push({ field: 'holderKind', code: 'required', message: 'Required.' });
  }
  const percentageRaw = record(body)['percentage'];
  const percentage = typeof percentageRaw === 'number' ? percentageRaw : Number(percentageRaw);
  if (!Number.isFinite(percentage) || percentage < 0 || percentage > 100) {
    errors.push({ field: 'percentage', code: 'out_of_range', message: '0–100.' });
  }
  const effectiveFrom = text(body, 'effectiveFrom');
  if (effectiveFrom === null || !ISO_DATE.test(effectiveFrom)) {
    errors.push({ field: 'effectiveFrom', code: 'required', message: 'Required.' });
  }
  const effectiveTo = text(body, 'effectiveTo');
  if (effectiveTo !== null && effectiveFrom !== null && effectiveTo < effectiveFrom) {
    errors.push({ field: 'effectiveTo', code: 'before_from', message: 'Before effectiveFrom.' });
  }
  if (errors.length > 0) {
    throw validation(errors);
  }
  if (
    owned === state.holdingLegalEntityId &&
    holderLegalEntityId !== null &&
    holderLegalEntityId !== state.holdingLegalEntityId
  ) {
    throw new FakeApiError(422, 'TEN_UNIDIRECTIONALITY');
  }
  const overlapping = state.ownership
    .filter((entry) => entry.ownedLegalEntityId === owned && entry.status === 'Active')
    .filter(
      (entry) =>
        (effectiveTo === null || entry.effectiveFrom <= effectiveTo) &&
        (entry.effectiveTo === null || entry.effectiveTo >= (effectiveFrom ?? '')),
    )
    .reduce((total, entry) => total + Number(entry.percentage), 0);
  if (overlapping + percentage > 100) {
    throw new FakeApiError(422, 'TEN_OWNERSHIP_TOTAL_EXCEEDED', {
      detail: `total ${String(overlapping + percentage)}% > 100%`,
    });
  }
  const created: OwnershipRecordResponse = {
    id: fakeTenancyId(state),
    ownedLegalEntityId: owned ?? '',
    holderKind: holderKind ?? 'Person',
    holderPersonId,
    holderLegalEntityId,
    holderType: text(body, 'holderType') ?? 'Other',
    percentage: Math.round(percentage * 100) / 100,
    effectiveFrom: effectiveFrom ?? '',
    effectiveTo,
    referenceDocumentId: text(body, 'referenceDocumentId'),
    note: text(body, 'note'),
    status: 'Active',
    createdAt: state.base,
    createdBy: null,
  };
  state.ownership.push(created);
  return created;
}

async function handleSiteRoutes(
  request: Request,
  tenant: FakeTenant,
  rest: readonly string[],
  ctx: TenancyRouteContext,
): Promise<Response | null> {
  const method = request.method.toUpperCase();
  const { state } = ctx;
  const url = new URL(request.url);
  if (rest.length === 0 && method === 'GET') {
    const status = url.searchParams.get('status');
    return jsonResponse(
      tenant.sites.filter((site) => (status ? site.status === status : true)).map(toSiteResponse),
    );
  }
  if (rest.length === 0 && method === 'POST') {
    const parsed = parseSite(tenant, await readJsonBody(request), state.base, null);
    parsed.id = fakeTenancyId(state);
    for (const version of parsed.calendarVersions) {
      version.id = fakeTenancyId(state);
      version.siteId = parsed.id;
    }
    tenant.sites.push(parsed);
    return jsonResponse(toSiteResponse(parsed), 201, { ETag: etagOf(parsed.version) });
  }
  const site = findSite(tenant, rest[0]);
  const today = civilDate(state.base, site.timeZoneId);
  if (rest.length === 1 && method === 'GET') {
    return jsonResponse(toSiteResponse(site), 200, { ETag: etagOf(site.version) });
  }
  if (rest.length === 1 && method === 'PUT') {
    requireIfMatch(request, etagOf(site.version));
    const parsed = parseSite(tenant, await readJsonBody(request), state.base, site);
    Object.assign(site, {
      name: parsed.name,
      timeZoneId: parsed.timeZoneId,
      phone: parsed.phone,
      status: parsed.status === 'Inactive' ? site.status : parsed.status,
      address: parsed.address,
      state: parsed.state,
      ibgeMunicipalityCode: parsed.ibgeMunicipalityCode,
      latitude: parsed.latitude,
      longitude: parsed.longitude,
      locationSource: parsed.locationSource,
      version: site.version + 1,
    });
    return jsonResponse(toSiteResponse(site), 200, { ETag: etagOf(site.version) });
  }
  if (rest.length === 2 && rest[1] === 'deactivate' && method === 'POST') {
    requireChangeReason(await readJsonBody(request));
    if (site.status === 'Inactive') {
      throw new FakeApiError(409, 'TEN_SITE_ALREADY_INACTIVE');
    }
    const future = site.appointments.filter((appointment) => appointment.scheduledAt > state.base);
    if (future.length > 0) {
      throw new FakeApiError(409, 'TEN_SITE_HAS_FUTURE_APPOINTMENTS', {
        detail: `${String(future.length)} confirmed future appointments`,
      });
    }
    site.status = 'Inactive';
    site.version += 1;
    return jsonResponse(toSiteResponse(site), 200, { ETag: etagOf(site.version) });
  }
  if (rest.length === 2 && rest[1] === 'geocode' && method === 'POST') {
    const definition = state.definitions.find(
      (entry) => entry.key === 'tenant.site.geocoding.enabled',
    );
    if (definition?.networkValue !== true) {
      throw new FakeApiError(422, 'TEN_GEOCODING_DISABLED');
    }
    site.latitude = -25.4284;
    site.longitude = -49.2733;
    site.locationSource = 'Geocoder';
    site.version += 1;
    return jsonResponse(toSiteResponse(site), 200, { ETag: etagOf(site.version) });
  }
  if (rest[1] === 'calendar-versions') {
    if (rest.length === 2 && method === 'GET') {
      return jsonResponse(
        [...site.calendarVersions].sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom)),
      );
    }
    if (rest.length === 2 && method === 'POST') {
      const body = await readJsonBody(request);
      const weekly = validateWeekly(body);
      const effectiveFrom = text(body, 'effectiveFrom') ?? today;
      if (!ISO_DATE.test(effectiveFrom)) {
        throw validation([
          { field: 'effectiveFrom', code: 'invalid_date', message: 'yyyy-MM-dd.' },
        ]);
      }
      if (effectiveFrom < today) {
        throw new FakeApiError(422, 'TEN_CALENDAR_EFFECTIVE_FROM_IN_PAST');
      }
      const latest = [...site.calendarVersions].sort((a, b) =>
        b.effectiveFrom.localeCompare(a.effectiveFrom),
      )[0];
      if (latest && latest.effectiveFrom >= effectiveFrom) {
        throw new FakeApiError(409, 'TEN_CALENDAR_VERSION_CONFLICT');
      }
      if (latest) {
        latest.effectiveTo = addDays(effectiveFrom, -1);
      }
      const created: CalendarVersionResponse = {
        id: fakeTenancyId(state),
        siteId: site.id,
        effectiveFrom,
        effectiveTo: null,
        weekly,
        note: text(body, 'note'),
        createdAt: state.base,
        createdBy: ctx.me.user.id,
      };
      site.calendarVersions.push(created);
      return jsonResponse(created, 201);
    }
    return null;
  }
  if (rest.length === 2 && rest[1] === 'effective-calendar' && method === 'GET') {
    const from = url.searchParams.get('from') ?? '';
    const to = url.searchParams.get('to') ?? '';
    const errors: FieldErrorItem[] = [];
    if (!from) {
      errors.push({ field: 'from', code: 'required', message: 'Required.' });
    } else if (!ISO_DATE.test(from)) {
      errors.push({ field: 'from', code: 'invalid_date', message: 'yyyy-MM-dd.' });
    }
    if (!to) {
      errors.push({ field: 'to', code: 'required', message: 'Required.' });
    } else if (!ISO_DATE.test(to)) {
      errors.push({ field: 'to', code: 'invalid_date', message: 'yyyy-MM-dd.' });
    } else if (to < from) {
      errors.push({ field: 'to', code: 'before_from', message: 'Before from.' });
    } else if (addDays(from, 366) <= to) {
      errors.push({ field: 'to', code: 'range_too_long', message: 'At most 366 days.' });
    }
    if (errors.length > 0) {
      throw validation(errors, 'TEN_INVALID_DATE_RANGE');
    }
    return jsonResponse(effectiveCalendar(site, from, to));
  }
  if (rest[1] === 'holidays') {
    if (rest.length === 2 && method === 'GET') {
      const year = url.searchParams.get('year');
      return jsonResponse(
        site.holidays
          .filter((holiday) => (year ? String(holiday.year) === year : true))
          .sort((a, b) => a.date.localeCompare(b.date)),
      );
    }
    if (rest.length === 2 && method === 'POST') {
      const body = await readJsonBody(request);
      const date = text(body, 'date') ?? '';
      const name = text(body, 'name');
      const errors: FieldErrorItem[] = [];
      if (!ISO_DATE.test(date)) {
        errors.push({ field: 'date', code: 'invalid_date', message: 'yyyy-MM-dd.' });
      }
      if (name === null) {
        errors.push({ field: 'name', code: 'required', message: 'Required.' });
      }
      if (errors.length > 0) {
        throw validation(errors);
      }
      if (date.slice(0, 4) < today.slice(0, 4)) {
        const reason = record(body)['changeReason'];
        if (
          typeof reason !== 'object' ||
          reason === null ||
          typeof (reason as { code?: unknown }).code !== 'string'
        ) {
          throw new FakeApiError(422, 'TEN_HOLIDAY_RETROACTIVE_REASON_REQUIRED');
        }
      }
      if (site.holidays.some((holiday) => holiday.date === date && holiday.name === name)) {
        throw new FakeApiError(409, 'TEN_DUPLICATE_HOLIDAY');
      }
      const created: HolidayResponse = {
        id: fakeTenancyId(state),
        siteId: site.id,
        scope: 'Site',
        state: null,
        ibgeMunicipalityCode: null,
        date,
        year: Number(date.slice(0, 4)),
        name: name ?? '',
        closesSite: record(body)['closesSite'] !== false,
        source: 'Manual',
      };
      site.holidays.push(created);
      return jsonResponse(created, 201);
    }
    if (rest.length === 3 && method === 'DELETE') {
      requireChangeReason(await readJsonBody(request));
      const index = site.holidays.findIndex((holiday) => holiday.id === rest[2]);
      if (index < 0) {
        throw new FakeApiError(404, 'PLT_NOT_FOUND');
      }
      site.holidays.splice(index, 1);
      return noContent();
    }
    return null;
  }
  if (rest[1] === 'closures') {
    if (rest.length === 2 && method === 'GET') {
      return jsonResponse(
        [...site.closures].sort((a, b) => a.startDate.localeCompare(b.startDate)),
      );
    }
    if (rest.length === 3 && rest[2] === 'preview-impact' && method === 'POST') {
      return jsonResponse(parseClosure(site, await readJsonBody(request), today).impact);
    }
    if (rest.length === 2 && method === 'POST') {
      const parsed = parseClosure(site, await readJsonBody(request), today);
      const closure: ClosureResponse = {
        id: fakeTenancyId(state),
        siteId: site.id,
        ...parsed.closure,
        createdAt: state.base,
        createdBy: ctx.me.user.id,
      };
      site.closures.push(closure);
      return jsonResponse({ closure, impact: parsed.impact }, 201);
    }
    if (rest.length === 3 && method === 'DELETE') {
      requireChangeReason(await readJsonBody(request));
      const index = site.closures.findIndex((closure) => closure.id === rest[2]);
      if (index < 0) {
        throw new FakeApiError(404, 'PLT_NOT_FOUND');
      }
      site.closures.splice(index, 1);
      return noContent();
    }
    return null;
  }
  return null;
}

async function handleTenantRoutes(
  request: Request,
  tenant: FakeTenant,
  rest: readonly string[],
  ctx: TenancyRouteContext,
): Promise<Response | null> {
  const method = request.method.toUpperCase();
  const { state } = ctx;
  const url = new URL(request.url);
  if (rest.length === 0 && method === 'GET') {
    return jsonResponse(toTenantDetail(tenant), 200, { ETag: etagOf(tenant.version) });
  }
  if (rest[0] === 'legal-entity' && rest.length === 1) {
    if (method === 'GET') {
      return jsonResponse(toLegalEntityResponse(tenant.legalEntity), 200, {
        ETag: etagOf(tenant.legalEntity.version),
      });
    }
    if (method === 'PUT') {
      requireIfMatch(request, etagOf(tenant.legalEntity.version));
      const body = await readJsonBody(request);
      requireChangeReason(body);
      const legalEntityBody = record(body)['legalEntity'];
      if (typeof legalEntityBody !== 'object' || legalEntityBody === null) {
        throw validation([{ field: 'legalEntity', code: 'required', message: 'Required.' }]);
      }
      const parsed = parseLegalEntity(state, legalEntityBody, tenant.legalEntity);
      tenant.legalEntity = {
        ...tenant.legalEntity,
        ...parsed,
        version: tenant.legalEntity.version + 1,
      };
      return jsonResponse(toLegalEntityResponse(tenant.legalEntity), 200, {
        ETag: etagOf(tenant.legalEntity.version),
      });
    }
    return null;
  }
  if (
    rest[0] === 'lifecycle' &&
    rest[1] === 'transitions' &&
    rest.length === 2 &&
    method === 'GET'
  ) {
    return jsonResponse(
      [...tenant.transitions].sort((a, b) => b.decidedAt.localeCompare(a.decidedAt)),
    );
  }
  if (rest[0] === 'sites') {
    return handleSiteRoutes(request, tenant, rest.slice(1), ctx);
  }
  if (rest[0] === 'shared-services-contract' && rest.length === 1) {
    if (method === 'GET') {
      if (!tenant.contract) {
        throw new FakeApiError(404, 'PLT_NOT_FOUND');
      }
      return jsonResponse(toContractResponse(tenant.contract), 200, {
        ETag: etagOf(tenant.contract.version),
      });
    }
    if (method === 'POST' || method === 'PUT') {
      if (method === 'POST' && tenant.contract) {
        throw new FakeApiError(409, 'TEN_CONTRACT_REF_EXISTS');
      }
      if (method === 'PUT' && !tenant.contract) {
        throw new FakeApiError(404, 'PLT_NOT_FOUND');
      }
      if (method === 'PUT' && tenant.contract) {
        requireIfMatch(request, etagOf(tenant.contract.version));
      }
      const body = await readJsonBody(request);
      if (method === 'PUT') {
        requireChangeReason(body);
      }
      const errors: FieldErrorItem[] = [];
      const documentId = text(body, 'documentId');
      if (documentId === null || !UUID.test(documentId)) {
        errors.push({ field: 'documentId', code: 'required', message: 'Required.' });
      }
      const effectiveFrom = text(body, 'effectiveFrom');
      if (effectiveFrom === null || !ISO_DATE.test(effectiveFrom)) {
        errors.push({ field: 'effectiveFrom', code: 'required', message: 'Required.' });
      }
      const effectiveTo = text(body, 'effectiveTo');
      if (effectiveTo !== null && effectiveFrom !== null && effectiveTo < effectiveFrom) {
        errors.push({
          field: 'effectiveTo',
          code: 'before_from',
          message: 'Before effectiveFrom.',
        });
      }
      const amountRaw = record(body)['monthlyMinimumAmount'];
      const amount = amountRaw === null || amountRaw === undefined ? null : Number(amountRaw);
      if (amount !== null && (!Number.isFinite(amount) || amount < 0)) {
        errors.push({ field: 'monthlyMinimumAmount', code: 'negative', message: 'Must be ≥ 0.' });
      }
      const feeRule = text(body, 'feeRuleDescription');
      if (feeRule !== null && feeRule.length > 2000) {
        errors.push({
          field: 'feeRuleDescription',
          code: 'too_long',
          message: 'At most 2000 characters.',
        });
      }
      if (errors.length > 0) {
        throw validation(errors);
      }
      const contract = {
        id: tenant.contract?.id ?? fakeTenancyId(state),
        tenantId: tenant.id,
        documentId: documentId ?? '',
        effectiveFrom: effectiveFrom ?? '',
        effectiveTo,
        monthlyMinimumAmount: amount,
        feeRuleDescription: feeRule,
        status: text(body, 'status') ?? 'Active',
        modifiedAt: state.base,
        version: (tenant.contract?.version ?? 0) + 1,
      };
      tenant.contract = contract;
      return jsonResponse(toContractResponse(contract), method === 'POST' ? 201 : 200, {
        ETag: etagOf(contract.version),
      });
    }
    return null;
  }
  if (rest[0] === 'settings') {
    if (rest.length === 1 && method === 'GET') {
      const key = url.searchParams.get('key');
      if (key) {
        return jsonResponse([settingValue(state, tenant, key)]);
      }
      return jsonResponse(
        state.definitions.map((definition) => settingValue(state, tenant, definition.key)),
      );
    }
    const key = rest[1] ?? '';
    if (rest.length === 2 && method === 'PUT') {
      const definition = state.definitions.find((entry) => entry.key === key);
      if (!definition) {
        throw new FakeApiError(404, 'CFG_SETTING_UNKNOWN');
      }
      if (!definition.tenantOverridable || definition.requiresTwoKeys) {
        throw new FakeApiError(422, 'CFG_SETTING_NOT_TENANT_OVERRIDABLE');
      }
      const override = tenant.settingOverrides.get(key);
      requireIfMatch(request, override ? etagOf(override.version, 's') : '*');
      const body = await readJsonBody(request);
      requireChangeReason(body);
      const value = record(body)['value'];
      if (value === undefined || typeof value !== typeof definition.defaultValue) {
        throw new FakeApiError(422, 'CFG_SETTING_INVALID_VALUE');
      }
      tenant.settingOverrides.set(key, { value, version: (override?.version ?? 0) + 1 });
      const response = settingValue(state, tenant, key);
      return jsonResponse(response, 200, { ETag: response.eTag });
    }
    if (rest.length === 2 && method === 'DELETE') {
      const definition = state.definitions.find((entry) => entry.key === key);
      if (!definition) {
        throw new FakeApiError(404, 'CFG_SETTING_UNKNOWN');
      }
      requireChangeReason(await readJsonBody(request));
      if (!tenant.settingOverrides.delete(key)) {
        throw new FakeApiError(404, 'PLT_NOT_FOUND');
      }
      return noContent();
    }
    return null;
  }
  if (rest[0] === 'bootstrap-admin' && rest.length === 1) {
    if (method === 'GET') {
      if (!tenant.bootstrap) {
        throw new FakeApiError(404, 'PLT_NOT_FOUND');
      }
      return jsonResponse(tenant.bootstrap);
    }
    if (method === 'POST') {
      const result = defineBootstrap(state, tenant, await readJsonBody(request), '');
      return jsonResponse(result.bootstrap, result.status);
    }
    return null;
  }
  return null;
}

const TENANT_SUBROUTES = new Set([
  'legal-entity',
  'lifecycle',
  'sites',
  'shared-services-contract',
  'settings',
  'bootstrap-admin',
]);

export async function handleTenancyRoute(
  request: Request,
  segments: readonly string[],
  ctx: TenancyRouteContext,
): Promise<Response | null> {
  try {
    if (
      segments[0] === 'network' &&
      (segments[1] === 'tenants' ||
        segments[1] === 'tenants-overview' ||
        segments[1] === 'ownership-records')
    ) {
      if (!ctx.me.networkAccess.hasAccess) {
        // REQ-AUTHZ-026: única situação em que a API responde 403 (nunca revela o que existe).
        throw new FakeApiError(403, 'PLT_NETWORK_SCOPE_REQUIRED');
      }
      return await handleNetworkRoutes(request, segments.slice(1), ctx);
    }
    if (
      segments[0] === 'tenants' &&
      segments[1] &&
      (segments.length === 2 || TENANT_SUBROUTES.has(segments[2] ?? ''))
    ) {
      const tenantId = segments[1];
      const visible =
        ctx.me.networkAccess.hasAccess ||
        ctx.me.contexts.some((context) => context.tenantId === tenantId);
      if (!visible) {
        throw new FakeApiError(404, 'PLT_NOT_FOUND');
      }
      const tenant = findTenant(ctx.state, tenantId);
      return await handleTenantRoutes(request, tenant, segments.slice(2), ctx);
    }
    return null;
  } catch (error: unknown) {
    if (error instanceof FakeApiError) {
      return problemResponse(error.status, error.errorCode, ctx.correlationId, error.extra);
    }
    throw error;
  }
}

export type { ImpactedAppointment };
